import { debounce } from 'lodash-es';

import { Flex, Heading, Text } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { trackEvent } from '~/features/analytics/track-event';

import { useExperimentalSegmentEvents } from '../hooks';
import { CheckedIcon } from '../icons';

import { SectionHeader } from './section-header';
import { TestimonialCarousel } from './testimonials-carousel';

export type ExperienceTestimonialsSectionProps = StaticExperienceTestimonialsSectionProps;

export function ExperienceTestimonialsSection({
  testimonials,
  title,
  subtitle,
  description,
}: ExperienceTestimonialsSectionProps) {
  const isExperimentalTrackEventsEnabled = useExperimentalSegmentEvents('dino')?.variant === 'on';
  const handleInteraction = debounce(() => {
    if (isExperimentalTrackEventsEnabled) {
      trackEvent({
        name: 'ExperiencePage CTA Clicked',
        type: 'click',
        label: 'testimonial_scroll_clicked',
      });
    }
  }, 100);

  return (
    <Flex
      as='section'
      flexDirection='column'
      gap='56px'
      padding={{
        base: '56px 0px',
        md: '112px 0px',
      }}
    >
      <ContentContainer margin='0 auto' maxWidth='766px' width='100%'>
        <SectionHeader
          color='grey.500'
          descriptionSlot={<Text color='grey.500'>{description}</Text>}
          iconSlot={<CheckedIcon color='grey.500' height='18px' width='18px' />}
          subtitle={subtitle}
          titleSlot={
            <Heading
              as='h2'
              fontWeight='600'
              lineHeight='1.1'
              fontSize={{
                base: '2.25rem',
                md: '3.375rem',
              }}
            >
              {title}
            </Heading>
          }
        />
      </ContentContainer>
      <TestimonialCarousel testimonials={testimonials} onInteraction={handleInteraction} />
    </Flex>
  );
}

export function transformStaticExperienceTestimonialsSectionProps() {
  return {
    title: 'Careers transformed, futures redefined',
    subtitle: 'Outcomes That Matter',
    description: 'These are the learners who did the work, changed their careers, and sometimes their lives.',
    testimonials: [
      {
        imagePath: '/images/experiments/ab_plp_redesign_2025/federico-martini.png',
        labelKey: 'testimonials.federicoMartini.label',
        quoteKey: 'testimonials.federicoMartini.quote',
        name: 'Federico Martini',
        title: 'AI Engineering Manager & Tech Leader',
        quoteColor: 'light',
        labelColor: 'dark',
      },
      {
        imagePath: '/images/experiments/ab_plp_redesign_2025/sabrina-palis.png',
        labelKey: 'testimonials.sabrinaPalis.label',
        quoteKey: 'testimonials.sabrinaPalis.quote',
        name: 'Sabrina Palis',
        title: 'AI & Data Consultant',
        quoteColor: 'light',
        labelColor: 'dark',
      },
      {
        imagePath: '/images/experiments/ab_plp_redesign_2025/allan-bravos.png',
        labelKey: 'testimonials.allanDiegoBravos.label',
        quoteKey: 'testimonials.allanDiegoBravos.quote',
        name: 'Allan Diego Bravos',
        title: 'Head of Data & AI',
        quoteColor: 'light',
        labelColor: 'light',
      },
      {
        imagePath: '/images/experiments/ab_plp_redesign_2025/jordan-shephard.png',
        labelKey: 'testimonials.jordanShephard.label',
        quoteKey: 'testimonials.jordanShephard.quote',
        name: 'Jordan Shephard',
        title: 'Product Manager',
        quoteColor: 'dark',
        labelColor: 'dark',
      },
      {
        imagePath: '/images/experiments/ab_plp_redesign_2025/rey-rosales.png',
        labelKey: 'testimonials.reyRosales.label',
        quoteKey: 'testimonials.reyRosales.quote',
        name: 'Rey Rosales',
        title: 'Professor',
        quoteColor: 'light',
        labelColor: 'light',
      },
      {
        imagePath: '/images/experiments/ab_plp_redesign_2025/subramanya-nataraj.png',
        labelKey: 'testimonials.subramanyaNataraj.label',
        quoteKey: 'testimonials.subramanyaNataraj.quote',
        name: 'Subramanya Nataraj',
        title: 'Research Assistant',
        quoteColor: 'light',
        labelColor: 'dark',
      },
    ],
  };
}

export type StaticExperienceTestimonialsSectionProps = ReturnType<
  typeof transformStaticExperienceTestimonialsSectionProps
>;
